/**
 * TransportSelector — selects SSE or long-polling based on environment config
 * and runtime health.
 *
 * Kill switch: set VITE_PARLANT_TRANSPORT to 'sse' | 'polling' | 'auto'.
 * Default is 'auto', which prefers SSE and demotes to long-polling after
 * DEMOTE_AFTER consecutive SSE failures.
 *
 * Promotion: after PROMOTE_COOLOFF_MS without a failure, SSE is re-attempted
 * if the previous mode was polling due to auto-demotion.
 */

import { SseTransport } from './SseTransport';
import { LongPollTransport } from './LongPollTransport';
import type { ChatTransport, ChatTransportEventMap, ChatTransportEventType, TransportName } from './ChatTransport';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listener<T = any> = (data: T) => void;

export interface TransportSelectorOptions {
  serverUrl: string;
  authToken?: string;
  /** Override from VITE_PARLANT_TRANSPORT env var. */
  forced?: TransportName | 'auto';
  /** Consecutive SSE failures before demoting (default 3). */
  demoteAfter?: number;
  /** Milliseconds before re-promoting SSE after demotion (default 5 min). */
  promoteCooloffMs?: number;
}

const ENV_TRANSPORT =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PARLANT_TRANSPORT) ??
  'auto';

export class TransportSelector implements ChatTransport {
  private inner: ChatTransport;
  private readonly opts: Required<TransportSelectorOptions>;

  private sseFailures = 0;
  private demotedAt: number | null = null;
  private currentName: TransportName;

  // Forwarding listener registry
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _listeners: Map<string, Set<Listener<any>>> = new Map();

  constructor(opts: TransportSelectorOptions) {
    this.opts = {
      serverUrl: opts.serverUrl,
      authToken: opts.authToken ?? '',
      forced: opts.forced ?? (ENV_TRANSPORT as TransportName | 'auto'),
      demoteAfter: opts.demoteAfter ?? Number(
        (typeof import.meta !== 'undefined' &&
          (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PARLANT_SSE_DEMOTE_AFTER) ?? 3
      ),
      promoteCooloffMs: opts.promoteCooloffMs ?? 5 * 60 * 1_000,
    };

    const useSse = this.opts.forced !== 'polling';
    this.currentName = useSse ? 'sse' : 'polling';
    this.inner = this._buildTransport(this.currentName);
  }

  on<K extends ChatTransportEventType>(
    event: K,
    listener: Listener<ChatTransportEventMap[K]>
  ): void {
    let set = this._listeners.get(event);
    if (!set) {
      set = new Set();
      this._listeners.set(event, set);
    }
    set.add(listener);
    this.inner.on(event, listener);
  }

  off<K extends ChatTransportEventType>(
    event: K,
    listener: Listener<ChatTransportEventMap[K]>
  ): void {
    this._listeners.get(event)?.delete(listener);
    this.inner.off(event, listener);
  }

  start(sessionId: string, lastOffset: number): void {
    if (this.opts.forced === 'auto') {
      this._maybePromote(sessionId, lastOffset);
    } else {
      this.inner.start(sessionId, lastOffset);
    }
  }

  stop(): void {
    this.inner.stop();
  }

  // -------------------------------------------------------------------------
  // Auto promotion / demotion
  // -------------------------------------------------------------------------

  private _maybePromote(sessionId: string, lastOffset: number): void {
    if (
      this.currentName === 'polling' &&
      this.demotedAt !== null &&
      Date.now() - this.demotedAt > this.opts.promoteCooloffMs
    ) {
      this._switchTo('sse', sessionId, lastOffset);
      this._emit('transportChange', 'sse');
    } else {
      this.inner.start(sessionId, lastOffset);
    }
  }

  /**
   * Called by the React layer when the SSE transport emits an error that
   * looks like a connection failure (not a 4xx auth error).
   * Call this from useChatTransport's error handler.
   */
  recordSseFailure(sessionId: string, lastOffset: number): void {
    if (this.currentName !== 'sse' || this.opts.forced === 'sse') return;

    this.sseFailures++;
    if (this.sseFailures >= this.opts.demoteAfter) {
      this.demotedAt = Date.now();
      this._switchTo('polling', sessionId, lastOffset);
      this._emit('metric', { name: 'demote', value: this.sseFailures });
      this._emit('transportChange', 'polling');
      this.sseFailures = 0;
    }
  }

  get transportName(): TransportName {
    return this.currentName;
  }

  // -------------------------------------------------------------------------

  private _switchTo(name: TransportName, sessionId: string, lastOffset: number): void {
    this.inner.stop();

    // Detach all forwarding listeners from old transport
    this._listeners.forEach((set, event) => {
      set.forEach(fn => this.inner.off(event as ChatTransportEventType, fn));
    });

    this.inner = this._buildTransport(name);
    this.currentName = name;

    // Attach all forwarding listeners to new transport
    this._listeners.forEach((set, event) => {
      set.forEach(fn => this.inner.on(event as ChatTransportEventType, fn));
    });

    this.inner.start(sessionId, lastOffset);
  }

  private _buildTransport(name: TransportName): ChatTransport {
    if (name === 'sse') {
      return new SseTransport({
        serverUrl: this.opts.serverUrl,
        authToken: this.opts.authToken || undefined,
      });
    }
    return new LongPollTransport({
      serverUrl: this.opts.serverUrl,
      authToken: this.opts.authToken || undefined,
    });
  }

  private _emit<K extends ChatTransportEventType>(
    event: K,
    data: ChatTransportEventMap[K]
  ): void {
    this._listeners.get(event)?.forEach(fn => fn(data));
  }
}
